import axios from "axios";
const API_BASE = "https://demo.commercialit.pk/api/"

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor to attach JWT token to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor to catch 401 unauthenticated
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An unexpected error occurred";

    if (error.response?.status === 401 && !window.location.pathname.includes("/login")) {
      // Token expired or invalid
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    return Promise.reject(new Error(message));
  }
);

// Auth & Users
export const authApi = {
  login: (data) => apiClient.post("/auth/login", data),
  getCurrentUser: () => apiClient.get("/auth/me"),
  changePassword: (data) => apiClient.put("/auth/change-password", data),
  getUsers: () => apiClient.get("/auth/users"),
  createUser: (data) => apiClient.post("/auth/users", data),
  updateUser: (id, data) => apiClient.put(`/auth/users/${id}`, data),
  toggleUserStatus: (id, status) => apiClient.patch(`/auth/users/${id}/status`, { status }),
  deleteUser: (id) => apiClient.delete(`/auth/users/${id}`),
};

// Vendors
export const vendorApi = {
  getAll: (params) => apiClient.get("/vendors", { params }),
  getById: (id) => apiClient.get(`/vendors/${id}`),
  create: (data) => apiClient.post("/vendors", data),
  update: (id, data) => apiClient.put(`/vendors/${id}`, data),
  delete: (id) => apiClient.delete(`/vendors/${id}`),
};

// Clients
export const clientApi = {
  getAll: (params) => apiClient.get("/clients", { params }),
  getById: (id) => apiClient.get(`/clients/${id}`),
  create: (data) => apiClient.post("/clients", data),
  update: (id, data) => apiClient.put(`/clients/${id}`, data),
  delete: (id) => apiClient.delete(`/clients/${id}`),
};

// Categories & Subcategories
export const categoryApi = {
  getCategories: () => apiClient.get("/categories"),
  createCategory: (data) => apiClient.post("/categories", data),
  updateCategory: (id, data) => apiClient.put(`/categories/${id}`, data),
  deleteCategory: (id) => apiClient.delete(`/categories/${id}`),

  getSubcategories: (categoryId) =>
    apiClient.get(`/categories/subcategories/all${categoryId ? `?category_id=${categoryId}` : ""}`),
  createSubcategory: (data) => apiClient.post("/categories/subcategories", data),
  updateSubcategory: (id, data) => apiClient.put(`/categories/subcategories/${id}`, data),
  deleteSubcategory: (id) => apiClient.delete(`/categories/subcategories/${id}`),
};

// Inventory Items
export const inventoryApi = {
  getAll: (params) => apiClient.get("/inventory", { params }),
  getById: (id) => apiClient.get(`/inventory/${id}`),
  getBySku: (sku) => apiClient.get(`/inventory/sku/${sku}`),
  scanLookup: (code) => apiClient.get(`/inventory/scan/${encodeURIComponent(code)}`),
  create: (data) => apiClient.post("/inventory", data),
  createBatch: (data) => apiClient.post("/inventory/batch", data),
  update: (id, data) => apiClient.put(`/inventory/${id}`, data),
  delete: (id) => apiClient.delete(`/inventory/${id}`),
};

// Purchases
export const purchaseApi = {
  getAll: (params) => apiClient.get("/purchases", { params }),
  getById: async (id) => {
    try {
      const res = await apiClient.get(`/purchases/${encodeURIComponent(id)}`);
      if (res && (res.data || res.id)) return res;
      throw new Error("Purchase not found");
    } catch (err) {
      if (typeof id === "string" && (id.startsWith("BILL") || isNaN(id))) {
        try {
          const fallbackRes = await apiClient.get("/purchases", { params: { bill_number: id } });
          const list = fallbackRes?.data || (Array.isArray(fallbackRes) ? fallbackRes : []);
          const matched = list.find(
            (p) => String(p.bill_number).trim().toLowerCase() === String(id).trim().toLowerCase()
          ) || list[0];
          if (matched) return { data: matched };
        } catch (fbErr) {
          // ignore fallback error
        }
      }
      throw err;
    }
  },
  create: (data) => apiClient.post("/purchases", data),
  update: (id, data) => apiClient.put(`/purchases/${encodeURIComponent(id)}`, data),
  delete: (id) => apiClient.delete(`/purchases/${id}`),
};

// Sales
export const saleApi = {
  getAll: (params) => apiClient.get("/sales", { params }),
  getById: async (id) => {
    try {
      const res = await apiClient.get(`/sales/${encodeURIComponent(id)}`);
      if (res && (res.data || res.id)) return res;
      throw new Error("Sale not found");
    } catch (err) {
      if (typeof id === "string" && (id.startsWith("INV") || isNaN(id))) {
        try {
          const fallbackRes = await apiClient.get("/sales", { params: { invoice_number: id } });
          const list = fallbackRes?.data || (Array.isArray(fallbackRes) ? fallbackRes : []);
          const matched = list.find(
            (s) => String(s.invoice_number).trim().toLowerCase() === String(id).trim().toLowerCase()
          ) || list[0];
          if (matched) return { data: matched };
        } catch (fbErr) {
          // ignore fallback error
        }
      }
      throw err;
    }
  },
  create: (data) => apiClient.post("/sales", data),
  update: (id, data) => apiClient.put(`/sales/${encodeURIComponent(id)}`, data),
  delete: (id) => apiClient.delete(`/sales/${id}`),
};

// Purchase Returns
export const purchaseReturnApi = {
  getAll: (params) => apiClient.get("/purchase-returns", { params }),
  getById: (id) => apiClient.get(`/purchase-returns/${encodeURIComponent(id)}`),
  getBillReturnableItems: (billNumber) =>
    apiClient.get(`/purchase-returns/bill/${encodeURIComponent(billNumber)}/items`),
  create: (data) => apiClient.post("/purchase-returns", data),
  delete: (id) => apiClient.delete(`/purchase-returns/${id}`),
  parseSheet: (formData) =>
    apiClient.post("/purchase-returns/parse-sheet", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// Sale Returns
export const saleReturnApi = {
  getAll: (params) => apiClient.get("/sale-returns", { params }),
  getById: (id) => apiClient.get(`/sale-returns/${encodeURIComponent(id)}`),
  getInvoiceReturnableItems: (invoiceNumber) =>
    apiClient.get(`/sale-returns/invoice/${encodeURIComponent(invoiceNumber)}/items`),
  create: (data) => apiClient.post("/sale-returns", data),
  delete: (id) => apiClient.delete(`/sale-returns/${id}`),
  parseSheet: (formData) =>
    apiClient.post("/sale-returns/parse-sheet", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// Dashboard & Reports
export const dashboardApi = {
  getSummary: () => apiClient.get("/dashboard/summary"),
  getAlerts: () => apiClient.get("/dashboard/alerts"),
  getStockReport: (params) => apiClient.get("/dashboard/stock", { params }),
  getWarrantyReport: (params) => apiClient.get("/dashboard/warranty", { params }),
  getUserAuditReport: (params) => apiClient.get("/dashboard/user-audit", { params }),
  getStockMovementReport: (params) => apiClient.get("/dashboard/stock-movement", { params }),
  getItemHistory: (params) => apiClient.get("/dashboard/item-history", { params }),
};

export default apiClient;
